import type {
  EpisodeProposal,
  EpisodeTarget,
  LinkedAction,
  LinkedOutreach,
} from '@troyai/portal/intervention-episodes/data-access';
import { EPISODE_AGENT_IDENTITY } from '@troyai/portal/intervention-episodes/data-access';
import {
  DEMO_ACTORS,
  SCRIPTED_ID_BASE,
  demoMemberId,
  storyIso,
} from '@troyai/portal/investor-demo/core';

import type { DemoEpisode } from '../demo-data';
import { buildActivity, episodeEventId } from './episode-builders';
import {
  ACTION_IDS,
  EPISODE_IDS,
  PROPOSAL_ID_BASE,
  TARGET_ID_BASE,
  outreachId,
  recommendationId,
} from './ids';

/**
 * The four episodes of the story, as written for Tue 2026-10-13 09:30 (the dates here and below
 * are the scripted ones; `storyIso` moves each onto the real story day). Episode ids are
 * `SCRIPTED_ID_BASE +` the number shown:
 * - 9001 CHF medication review: active since Oct 6 16:05, owner Lisa, escalated to Dana at 09:14
 * - 9002 SDoH, transportation: active since Oct 6, owner Dana
 * - 9003 Endocrine, annual diabetic eye exam: active since Oct 6, owner Dana
 * - 9004 Preventive health, pneumonia vaccination: a draft the agent created at 08:45
 * Two of the CHF activity rows (Oct 14, Lisa) stay hidden until the "+3 days" jump.
 *
 * Every target of an active episode has an assignee: Dana for what the plan side does (and for the
 * two CHF medication targets the run sheet records as her), Troy Agent for what it checks from
 * claims or by its check-in calls. The draft's stay unassigned.
 */

const DANA = DEMO_ACTORS.dana.email;
const LISA = DEMO_ACTORS.lisa.email;
const AGENT = DEMO_ACTORS.agent.email;

/** Proposals adopt a recommendation; the adoption ids are only ever read as opaque numbers. */
const ADOPTION_ID_BASE = SCRIPTED_ID_BASE + 9450;

/** Fields every demo episode shares: first attempt, nothing earlier. Fresh on every call. */
function firstAttempt(): Pick<
  DemoEpisode,
  'tenant_id' | 'member_global_id' | 'attempt_number' | 'attempt_count' | 'attempt_history'
> {
  return {
    tenant_id: 1,
    member_global_id: demoMemberId(),
    attempt_number: 1,
    attempt_count: 1,
    attempt_history: [],
  };
}

function proposal(
  n: number,
  recommendationRow: number,
  templateName: string,
  createdAt: string
): EpisodeProposal {
  return {
    proposal_id: PROPOSAL_ID_BASE + n,
    recommendation_id: recommendationId(recommendationRow),
    recommendation_version: 1,
    adoption_id: ADOPTION_ID_BASE + n,
    status: 'planned',
    created_at: createdAt,
    source_recommendation_rejected: false,
    template_name: templateName,
  };
}

/**
 * A target without observations. `metricKey` is the bold line on the card (the page words it with
 * `sentenceCase`), `description` the spec's target title. `dueDay` is `yyyy-MM-dd`; the backend
 * sends midnight UTC and the page reads only the day. `assignee` is an email, the agent's identity
 * or `''` (unassigned).
 */
function target(
  n: number,
  metricKey: string,
  description: string,
  required: boolean,
  dueDay: string | null,
  createdAt: string,
  assignee = ''
): EpisodeTarget {
  return {
    target_id: TARGET_ID_BASE + n,
    metric_key: metricKey,
    description,
    baseline: {},
    required,
    ...(dueDay ? { due_date: `${dueDay}T00:00:00Z` } : {}),
    evaluation_contract: null,
    created_at: createdAt,
    observations: [],
    assignee_identity: assignee,
  };
}

/**
 * Gives an active episode the `target_assigned` event of each target that has an assignee: the
 * owner's, at the instant of the activation and after its row. Event ids continue the episode's
 * numbering (`buildActivity` counts them from 1), so no existing id moves. Fixtures of both
 * stories use it; a draft has no activation and keeps its targets unassigned.
 */
export function withAssignments(episode: DemoEpisode): DemoEpisode {
  const at = episode.activated_at;
  if (!at) {
    return episode;
  }
  // Rows are oldest first: the new row goes before the first one that is later
  const place = <T extends { occurred_at: string }>(rows: T[], row: T): void => {
    const later = rows.findIndex((candidate) => candidate.occurred_at > at);
    rows.splice(later < 0 ? rows.length : later, 0, row);
  };
  let events = episode.events.length;
  for (const assigned of episode.targets.filter((candidate) => candidate.assignee_identity)) {
    const eventId = episodeEventId(episode.intervention_episode_id, ++events);
    // Two copies, so changing one never changes the other
    const payload = (): Record<string, unknown> => ({
      target_id: assigned.target_id,
      previous_assignee_identity: '',
      assignee_identity: assigned.assignee_identity,
    });
    place(episode.events, {
      event_id: eventId,
      event_type: 'target_assigned',
      actor_identity: episode.owner_identity,
      payload: payload(),
      occurred_at: at,
    });
    place(episode.recommendation_lifecycle, {
      source_kind: 'episode_event',
      source_id: eventId,
      event_type: 'target_assigned',
      occurred_at: at,
      payload: payload(),
    });
  }
  return episode;
}

function chfEpisode(): DemoEpisode {
  const createdAt = storyIso('2026-10-06T16:05:00');
  const fax: LinkedAction = {
    action_global_id: ACTION_IDS.faxToPatel,
    action_template_id: SCRIPTED_ID_BASE + 9650,
    title:
      'Fax to Dr. Patel (orthopedics): discontinuation request + topical diclofenac alternative',
    status: 'completed',
    due_date: storyIso('2026-10-07'),
    created_at: storyIso('2026-10-07T14:40:00'),
    linked_by: AGENT,
    linked_at: storyIso('2026-10-07T14:40:00'),
  };
  const call: LinkedOutreach = {
    member_outreach_id: outreachId(1),
    outreach_channel: 'voice-call',
    direction: 'outbound',
    outcome: 'completed',
    purposes: ['care_coordination'],
    created_by: AGENT,
    created_at: storyIso('2026-10-07T11:20:00'),
    directly_linked: true,
  };
  return {
    ...firstAttempt(),
    intervention_episode_id: EPISODE_IDS.chf,
    status: 'active',
    objective:
      'Congestive heart failure — stop meloxicam (NSAID) given CHF and CKD 3a; switch to topical diclofenac',
    owner_identity: LISA,
    version: 2,
    created_by: LISA,
    activated_by: LISA,
    created_at: createdAt,
    updated_at: storyIso('2026-10-13T09:14:00'),
    activated_at: createdAt,
    proposals: [proposal(1, 1, 'Discontinue meloxicam, start topical diclofenac', createdAt)],
    targets: [
      target(
        1,
        'medication_discontinuation',
        'Meloxicam discontinued',
        true,
        storyIso('2026-10-20'),
        createdAt,
        DANA
      ),
      target(
        4,
        'medication_start',
        'Topical diclofenac started',
        true,
        storyIso('2026-10-20'),
        createdAt,
        DANA
      ),
      target(
        2,
        'daily_weight_logging',
        'Daily weight logged ≥5 days/week',
        false,
        storyIso('2026-11-06'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
      target(
        3,
        'ed_visit_free_period',
        'No ED visit for 30 days',
        false,
        storyIso('2026-11-06'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
    ],
    activity: { actions: [fax], outreach_attempts: [call] },
    ...buildActivity(EPISODE_IDS.chf, [
      {
        kind: 'episode',
        type: 'activated',
        actor: LISA,
        at: createdAt,
        title: 'Recommendation accepted; episode activated',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-07T11:20:00'),
        title: 'Outreach call, 9 min',
        note: 'Explained the plan, confirmed scale at home.',
      },
      {
        kind: 'episode',
        type: 'action_linked',
        actor: AGENT,
        at: storyIso('2026-10-07T14:40:00'),
        title: 'Fax sent to Dr. Patel (orthopedics)',
        note: 'Discontinuation request + alternative (topical diclofenac).',
      },
      {
        kind: 'episode',
        type: 'outreach_linked',
        actor: AGENT,
        at: storyIso('2026-10-11T15:02:00'),
        title: 'Outreach call, 6 min',
        note: 'Member confirmed daily weights started; asked whether she can "just stop the pain pill". Not answered, flagged for pharmacist.',
      },
      {
        kind: 'episode',
        type: 'escalated',
        actor: AGENT,
        at: storyIso('2026-10-13T09:14:00'),
        title: 'Escalated to Dana Whitfield',
        note: 'Orthopedist has not responded in 4 business days; member reports still taking meloxicam "for the knee".',
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: LISA,
        at: storyIso('2026-10-14T10:05:00'),
        title:
          "Call to Dr. Patel's office: agreed to stop meloxicam; diclofenac gel sent to the pharmacy",
        visibleFrom: storyIso('2026-10-16'),
      },
      {
        kind: 'episode',
        type: 'note_added',
        actor: LISA,
        at: storyIso('2026-10-14T11:40:00'),
        title: 'Call to member: explained the change; stopping meloxicam today',
        visibleFrom: storyIso('2026-10-16'),
      },
    ]),
  };
}

function sdohEpisode(): DemoEpisode {
  const createdAt = storyIso('2026-10-06T16:20:00');
  const hraCall: LinkedOutreach = {
    member_outreach_id: outreachId(2),
    outreach_channel: 'voice-call',
    direction: 'outbound',
    outcome: 'completed',
    purposes: ['hra'],
    created_by: AGENT,
    created_at: storyIso('2026-09-24T09:48:00'),
    directly_linked: true,
  };
  return {
    ...firstAttempt(),
    intervention_episode_id: EPISODE_IDS.sdoh,
    status: 'active',
    objective: 'SDoH, transportation',
    owner_identity: DANA,
    version: 2,
    created_by: DANA,
    activated_by: DANA,
    created_at: createdAt,
    updated_at: storyIso('2026-10-06T16:22:00'),
    activated_at: storyIso('2026-10-06T16:22:00'),
    proposals: [proposal(2, 2, 'Transportation support (ride benefit)', createdAt)],
    targets: [
      target(
        4,
        'transportation_access',
        'Ride benefit set up',
        true,
        storyIso('2026-10-30'),
        createdAt,
        DANA
      ),
    ],
    activity: { actions: [], outreach_attempts: [hraCall] },
    ...buildActivity(EPISODE_IDS.sdoh, [
      {
        kind: 'recommendation',
        type: 'accepted',
        actor: DANA,
        at: storyIso('2026-10-06T16:18:00'),
      },
      {
        kind: 'episode',
        type: 'created',
        actor: DANA,
        at: createdAt,
        title: 'Episode started',
      },
      {
        kind: 'episode',
        type: 'activated',
        actor: DANA,
        at: storyIso('2026-10-06T16:22:00'),
        title: 'Episode activated',
      },
    ]),
  };
}

function eyeExamEpisode(): DemoEpisode {
  const createdAt = storyIso('2026-10-06T16:26:00');
  return {
    ...firstAttempt(),
    intervention_episode_id: EPISODE_IDS.eyeExam,
    status: 'active',
    objective: 'Endocrine, annual diabetic eye exam',
    owner_identity: DANA,
    version: 2,
    created_by: DANA,
    activated_by: DANA,
    created_at: createdAt,
    updated_at: storyIso('2026-10-06T16:28:00'),
    activated_at: storyIso('2026-10-06T16:28:00'),
    proposals: [proposal(3, 3, 'Annual diabetic eye exam', createdAt)],
    targets: [
      target(
        5,
        'diabetic_eye_exam',
        'Eye exam completed',
        true,
        storyIso('2026-12-15'),
        createdAt,
        EPISODE_AGENT_IDENTITY
      ),
    ],
    activity: { actions: [], outreach_attempts: [] },
    ...buildActivity(EPISODE_IDS.eyeExam, [
      {
        kind: 'recommendation',
        type: 'accepted',
        actor: DANA,
        at: storyIso('2026-10-06T16:24:00'),
      },
      {
        kind: 'episode',
        type: 'created',
        actor: DANA,
        at: createdAt,
        title: 'Episode started',
      },
      {
        kind: 'episode',
        type: 'activated',
        actor: DANA,
        at: storyIso('2026-10-06T16:28:00'),
        title: 'Episode activated',
      },
    ]),
  };
}

function pneumoniaDraft(): DemoEpisode {
  const createdAt = storyIso('2026-10-13T08:45:00');
  return {
    ...firstAttempt(),
    intervention_episode_id: EPISODE_IDS.pneumonia,
    status: 'draft',
    objective: 'Preventive health, pneumonia vaccination',
    owner_identity: DANA,
    version: 1,
    created_by: AGENT,
    created_at: createdAt,
    updated_at: createdAt,
    proposals: [proposal(4, 4, 'Pneumococcal vaccination (PCV20)', createdAt)],
    targets: [
      target(
        6,
        'pneumococcal_vaccination',
        'Pneumococcal vaccine (PCV20) given',
        true,
        storyIso('2027-01-11'),
        createdAt
      ),
      target(
        7,
        'pcp_vaccination_discussion',
        'PCP conversation about vaccination',
        false,
        null,
        createdAt
      ),
    ],
    activity: { actions: [], outreach_attempts: [] },
    ...buildActivity(EPISODE_IDS.pneumonia, [
      { kind: 'recommendation', type: 'generated', at: storyIso('2026-10-06T10:30:00') },
      {
        kind: 'recommendation',
        type: 'accepted',
        actor: LISA,
        at: storyIso('2026-10-13T08:42:00'),
      },
      {
        kind: 'episode',
        type: 'created',
        actor: AGENT,
        at: createdAt,
        title: 'Episode drafted',
      },
    ]),
  };
}

/** The four episodes: CHF, SDoH and eye exam active, the pneumonia vaccination a draft. */
export function seedEpisodes(): DemoEpisode[] {
  return [
    withAssignments(chfEpisode()),
    withAssignments(sdohEpisode()),
    withAssignments(eyeExamEpisode()),
    pneumoniaDraft(),
  ];
}
